import { createReducer, on } from '@ngrx/store';
import { Update } from '@ngrx/entity';

import { Entity, User } from '@apps/auth/domain/models/back';
import { Site } from '@apps/priv/models/site';
import * as fromBackAuthEntitiesGlobalActions from './entities-global.actions';
import { usersAdapter } from '@apps/back/store/auth/users/users.common';

import { DetailedEntity, sitesAdapter } from '../entities.common';
import { AppliRole } from '@apps/back/models';

export const BackAuthEntitiesGlobalFeatureKey = 'global';
export interface IBackAuthEntitiesGlobalState
{
    selected: DetailedEntity;
    qsWordImpeUsers: string;
    entityUsersQueryParameters: Map<string, any>;
}
export const BackAuthEntitiesGlobalInitialState: IBackAuthEntitiesGlobalState =
{
    selected: new DetailedEntity(),
    qsWordImpeUsers: '',
    entityUsersQueryParameters: new Map<string, any>()
};

export const BackAuthEntitiesGlobalReducer = createReducer(
    BackAuthEntitiesGlobalInitialState,
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityDetailsSucceeded,
        (state: IBackAuthEntitiesGlobalState, { entity }: { entity: Entity }) =>
            ({
                ...state,
                selected: {
                    ...state.selected,
                    entity
                }
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntitySelectedReset,
        () => BackAuthEntitiesGlobalInitialState
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityUsersSucceeded,
        (state: IBackAuthEntitiesGlobalState, { users }: { users: User[]; }) =>
            ({
                ...state,
                selected: {
                    ...state.selected,
                    users: usersAdapter.setAll(users, state.selected.users)
                }
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityReferentsSucceeded,
        (state: IBackAuthEntitiesGlobalState, { referents }: { referents: User[]; }) =>
            ({
                ...state,
                selected: {
                    ...state.selected,
                    referents: usersAdapter.setAll(referents, state.selected.referents)
                }
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityAddReferentSucceeded,
        (state: IBackAuthEntitiesGlobalState, { referents }: { referents: User[]; }) =>
            ({
                ...state,
                selected: {
                    ...state.selected,
                    referents: usersAdapter.setAll(referents, state.selected.referents)
                }
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityRemoveReferentSucceeded,
        (state: IBackAuthEntitiesGlobalState, { referents }: { referents: User[]; }) =>
            ({
                ...state,
                selected: {
                    ...state.selected,
                    referents: usersAdapter.setAll(referents, state.selected.referents)
                }
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntitySitesSucceeded,
        (state: IBackAuthEntitiesGlobalState, { sites }: { sites: Site[]; }) =>
            ({
                ...state,
                selected: {
                    ...state.selected,
                    sites: sitesAdapter.setAll(sites, state.selected.sites)
                }
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityUsersQsWordImpeSet,
        (state, { qsWordImpe }: { qsWordImpe: string; }) =>
            ({
                ...state,
                qsWordImpeUsers: qsWordImpe.replace(/[-/\\^$*+?.()|[\]{}]/g, '')
            })
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityUserSelectedSet,
        (state: IBackAuthEntitiesGlobalState, { user }: { user: User }) =>
        {
            const updateUser: Update<User> = {
                id: user.id,
                changes: {
                    ...user
                }
            };

            return {
                ...state,
                selected: {
                    ...state.selected,
                    users: usersAdapter.updateOne(updateUser, state.selected.users)
                }
            };
        }
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityAddHabilitationSucceeded,
        (state: IBackAuthEntitiesGlobalState, { appRole }: { appRole: AppliRole }) =>
        {
            const entity = state.selected.entity;

            entity.applications.push({
                application: appRole.application,
                defaultRole: AppliRole.RoleByLevel(appRole.role)
            });

            return {
                ...state,
                selected: {
                    ...state.selected,
                    entity: entity
                }
            };
        }
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityRemoveHabilitationSucceeded,
        (state: IBackAuthEntitiesGlobalState, { appRole }: { appRole: AppliRole }) =>
        {
            const entity = state.selected.entity;

            entity.applications = entity.applications.filter(a =>
                a.application !== appRole.application
            );

            return {
                ...state,
                selected: {
                    ...state.selected,
                    entity: entity
                }
            };
        }
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityUsersUpdateQueryStringRequested,
        (state: IBackAuthEntitiesGlobalState, param) =>
        {
            return {
                ...state,
                queryString: param.queryString
            };
        }
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityUsersAddQueryParameterRequested,
        (state: IBackAuthEntitiesGlobalState, queryParameter) =>
        {
            const queryParameters: Map<string, any> = state.entityUsersQueryParameters;
            queryParameters.set(queryParameter.key, queryParameter.value);

            return {
                ...state,
                entityUsersQueryParameters:  queryParameters
            };
        }
    ),
    on(
        fromBackAuthEntitiesGlobalActions.BackAuthEntityUsersRemoveQueryParameterRequested,
        (state: IBackAuthEntitiesGlobalState, queryParameter) =>
        {
            const queryParameters: Map<string, any> = state.entityUsersQueryParameters;
            queryParameters.delete(queryParameter.key);

            return {
                ...state,
                entityUsersQueryParameters:  queryParameters
            };
        }
    )
);
